import './library.css';

import { captureProductEvent, setAnalyticsUser } from './analytics';
import { cloud, CloudUnavailable } from './app/cloud';
import type {
  BillingSummary,
  Capabilities,
  CloudDocumentMeta,
  CloudPublication,
  CloudUser,
  Features,
} from './app/cloud';
import {
  DOCUMENT_LIBRARY_PATH,
  LOCAL_TEMPLATE_HANDOFF_KEY,
  editorDocumentPath,
} from './app/routes';
import { STARTER_TEMPLATES, starterDocument } from './app/templates';
import { registerBuiltinNodes } from './nodes';
import { el } from './ui/dom';
import { openAccountDialog } from './ui/account-dialog';
import { openAuthDialog } from './ui/auth-dialog';
import { openHandleDialog } from './ui/handle-dialog';
import { openPublicationDialog } from './ui/publication-dialog';
import { openUpgradeDialog } from './ui/upgrade-dialog';

registerBuiltinNodes();

type LibraryView = 'recent' | 'owned' | 'shared';

const emptyBilling: BillingSummary = {
  enabled: false,
  customer: false,
  entitlement: { key: 'pro', active: false, state: 'none', validUntil: null },
  subscription: null,
};

let user: CloudUser | null = null;
let features: Features = { rooms: true, googleAuth: false, previews: false };
let capabilities: Capabilities = { cloudWrite: false };
let documents: CloudDocumentMeta[] = [];
let billing = emptyBilling;
let available = true;
let loading = true;
let creating: string | null = null;
let view: LibraryView = 'recent';
let query = '';
let notice = '';
let accountMenuOpen = false;
let documentMenuOpen: string | null = null;
const publicationByDocument = new Map<string, CloudPublication | null>();
const publicationStatusLoading = new Set<string>();
let handlePromptOpen = false;
let oauthClient: { name?: string; uri?: string } | null = null;
let oauthConsentBusy = false;
let oauthConsentError = '';

const oauthConsentRequest = location.pathname === '/oauth/consent';
const oauthQuery = new URLSearchParams(location.search).has('sig')
  ? location.search.slice(1)
  : undefined;
const rootNode = document.getElementById('library-app');
if (!rootNode) throw new Error('Missing #library-app');
const root: HTMLElement = rootNode;

const relativeTime = (ms: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

async function refresh(): Promise<void> {
  loading = true;
  notice = '';
  render();
  try {
    const identity = await cloud.me();
    user = identity.user;
    setAnalyticsUser(user?.id);
    features = identity.features;
    capabilities = identity.capabilities;
    available = true;
    if (user) {
      [documents, billing, oauthClient] = await Promise.all([
        cloud.list(),
        cloud.billing(),
        oauthConsentRequest
          ? loadOAuthClient().catch((error) => {
              oauthConsentError = (error as Error).message;
              return null;
            })
          : Promise.resolve(null),
      ]);
    } else {
      documents = [];
      billing = emptyBilling;
    }
  } catch (error) {
    available = !(error instanceof CloudUnavailable);
    user = null;
    documents = [];
    notice =
      error instanceof CloudUnavailable
        ? 'Cloud services are offline. You can still design and export in an unsaved session.'
        : (error as Error).message;
  } finally {
    loading = false;
    render();
    if (user?.handleRequired && !handlePromptOpen) {
      handlePromptOpen = true;
      window.setTimeout(() =>
        openHandleDialog({
          onUser: (next) => {
            user = next;
            handlePromptOpen = false;
            render();
          },
          onSignOut: async () => {
            await cloud.signOut();
            setAnalyticsUser(null);
            window.location.reload();
          },
        }),
      );
    }
  }
}

function showAuth(initialMode: 'sign-in' | 'sign-up' = 'sign-in'): void {
  openAuthDialog({
    googleEnabled: features.googleAuth,
    initialMode,
    oauthQuery,
    signIn: (email, password) => cloud.signIn(email, password, oauthQuery),
    signUp: (email, password) => cloud.signUp(email, password, oauthQuery),
    onAuthenticated: refresh,
  });
}

async function createFromTemplate(templateId: string): Promise<void> {
  if (!user) {
    showAuth('sign-up');
    return;
  }
  if (!capabilities.cloudWrite) {
    try {
      const starter = starterDocument(templateId);
      sessionStorage.setItem(LOCAL_TEMPLATE_HANDOFF_KEY, starter.body);
      captureProductEvent(
        'texture_created',
        { template_id: templateId, storage_mode: 'local_session' },
        { sendInstantly: true },
      );
      window.location.assign('/app?template=starter');
    } catch (error) {
      notice = (error as Error).message;
      render();
    }
    return;
  }
  creating = templateId;
  notice = '';
  render();
  try {
    const starter = starterDocument(templateId);
    // Reserve a stable editor URL, but keep untouched starters out of
    // “My textures”. The editor publishes this draft on its first real change.
    const document = await cloud.create(starter.name, starter.body, { draft: true });
    captureProductEvent(
      'texture_created',
      { template_id: templateId, storage_mode: 'cloud_draft' },
      { sendInstantly: true },
    );
    window.location.assign(editorDocumentPath(document.id));
  } catch (error) {
    creating = null;
    notice = (error as Error).message;
    render();
  }
}

async function loadPublicationStatus(documentId: string): Promise<void> {
  if (publicationStatusLoading.has(documentId)) return;
  publicationStatusLoading.add(documentId);
  publicationByDocument.delete(documentId);
  render();
  try {
    publicationByDocument.set(documentId, await cloud.publication(documentId));
  } catch (error) {
    notice = (error as Error).message;
  } finally {
    publicationStatusLoading.delete(documentId);
    render();
  }
}

async function duplicateDocument(document: CloudDocumentMeta): Promise<void> {
  documentMenuOpen = null;
  if (!capabilities.cloudWrite) {
    notice = 'Upgrade to save a cloud copy.';
    render();
    return;
  }
  try {
    const source = await cloud.open(document.id);
    const copy = await cloud.create(`${document.name} copy`, source.body);
    documents = [copy, ...documents];
    notice = `Created “${copy.name}”`;
  } catch (error) {
    notice = (error as Error).message;
  }
  render();
}

function publishDocument(document: CloudDocumentMeta): void {
  documentMenuOpen = null;
  if (!capabilities.cloudWrite) {
    notice = 'Upgrade to publish or republish a texture.';
    render();
    return;
  }
  openPublicationDialog({
    documentId: document.id,
    documentName: document.name,
    status: (message) => {
      notice = message;
      render();
    },
    onPublished: (publication) => {
      publicationByDocument.set(document.id, publication);
      render();
    },
  });
  render();
}

async function unpublishDocument(document: CloudDocumentMeta): Promise<void> {
  documentMenuOpen = null;
  try {
    const publication = await cloud.publication(document.id);
    if (!publication || publication.unpublished) {
      notice = 'This texture is not currently published.';
      render();
      return;
    }
    if (
      !window.confirm(
        `Unpublish “${publication.name}” from the gallery? This hides the listing but cannot revoke its CC0 dedication.`,
      )
    )
      return;
    await cloud.unpublish(publication.id);
    publicationByDocument.set(document.id, { ...publication, unpublished: true });
    notice = `Unpublished “${publication.name}”`;
  } catch (error) {
    notice = (error as Error).message;
  }
  render();
}

async function removeDocument(document: CloudDocumentMeta): Promise<void> {
  documentMenuOpen = null;
  if (!window.confirm(`Move “${document.name}” to trash?`)) return;
  try {
    await cloud.remove(document.id);
    documents = documents.filter((item) => item.id !== document.id);
    publicationByDocument.delete(document.id);
    notice = `Moved “${document.name}” to trash`;
  } catch (error) {
    notice = (error as Error).message;
  }
  render();
}

function templateCard(template: (typeof STARTER_TEMPLATES)[number]): HTMLElement {
  const pending = creating === template.id;
  const visual = template.image
    ? el('img', { src: template.image, alt: '', loading: 'lazy' })
    : el('div', { class: 'blank-template', 'aria-hidden': 'true' }, [
        el('span', {}, ['+']),
        el('small', {}, ['Empty graph']),
      ]);
  return el(
    'button',
    {
      class: `template-card${template.id === 'blank' ? ' blank' : ''}`,
      type: 'button',
      disabled: pending,
      title: capabilities.cloudWrite
        ? template.name
        : `${template.name} · opens an unsaved free session`,
      onclick: () => void createFromTemplate(template.id),
    },
    [
      el('div', { class: 'template-visual' }, [visual]),
      el('span', { class: 'template-copy' }, [
        el('strong', {}, [pending ? 'Creating…' : template.name]),
        el('small', {}, [template.description]),
      ]),
    ],
  );
}

function documentCard(document: CloudDocumentMeta): HTMLElement {
  const preview = document.previewUrl
    ? el('img', { src: document.previewUrl, alt: '', loading: 'lazy' })
    : el('div', { class: 'document-placeholder', 'aria-hidden': 'true' }, [
        el('span', {}, [document.name.slice(0, 1).toUpperCase() || 'K']),
        el('i'),
        el('i'),
        el('i'),
      ]);
  const access = document.access ?? 'owner';
  const shared = access !== 'owner';
  const publication = publicationByDocument.get(document.id);
  return el('article', { class: 'document-card' }, [
    el(
      'a',
      {
        class: 'document-open',
        href: editorDocumentPath(document.id),
        'aria-label': `Open ${document.name}`,
      },
      [el('div', { class: 'document-preview' }, [preview])],
    ),
    el('div', { class: 'document-meta' }, [
      el('div', { class: 'document-title-line' }, [
        el('a', { href: editorDocumentPath(document.id), class: 'document-title' }, [
          document.name,
        ]),
        access === 'owner'
          ? el('div', { class: 'document-menu-shell' }, [
              el(
                'button',
                {
                  class: 'document-more',
                  type: 'button',
                  title: 'Texture actions',
                  'aria-label': `Actions for ${document.name}`,
                  'aria-haspopup': 'menu',
                  'aria-expanded': String(documentMenuOpen === document.id),
                  onclick: () => {
                    const opening = documentMenuOpen !== document.id;
                    documentMenuOpen = opening ? document.id : null;
                    accountMenuOpen = false;
                    render();
                    if (opening) void loadPublicationStatus(document.id);
                  },
                },
                ['•••'],
              ),
              documentMenuOpen === document.id
                ? el('div', { class: 'library-popover document-menu', role: 'menu' }, [
                    el(
                      'button',
                      {
                        type: 'button',
                        role: 'menuitem',
                        disabled: !capabilities.cloudWrite,
                        title: capabilities.cloudWrite
                          ? 'Make a copy'
                          : 'Upgrade to save a cloud copy',
                        onclick: () => void duplicateDocument(document),
                      },
                      ['Make a copy'],
                    ),
                    el(
                      'button',
                      {
                        type: 'button',
                        role: 'menuitem',
                        disabled: !capabilities.cloudWrite,
                        title: capabilities.cloudWrite
                          ? 'Publish a CC0 snapshot to the gallery'
                          : 'Upgrade to publish textures',
                        onclick: () => void publishDocument(document),
                      },
                      ['Publish'],
                    ),
                    ...(publication && !publication.unpublished
                      ? [
                          el(
                            'button',
                            {
                              type: 'button',
                              role: 'menuitem',
                              onclick: () => void unpublishDocument(document),
                            },
                            ['Unpublish'],
                          ),
                        ]
                      : []),
                    el(
                      'button',
                      {
                        type: 'button',
                        role: 'menuitem',
                        class: 'danger',
                        onclick: () => void removeDocument(document),
                      },
                      ['Delete'],
                    ),
                  ])
                : el('span'),
            ])
          : el('span', { class: 'access-pill' }, [access === 'editor' ? 'Can edit' : 'View only']),
      ]),
      el('span', { class: 'document-date' }, [
        `${shared ? 'Shared · ' : 'Edited '}${relativeTime(document.updatedAt)}`,
      ]),
    ]),
  ]);
}

const signOut = (): void => {
  void cloud.signOut().then(() => {
    user = null;
    setAnalyticsUser(null);
    documents = [];
    publicationByDocument.clear();
    accountMenuOpen = false;
    render();
  });
};

function accountMenu(): HTMLElement {
  if (!user) {
    return el('div', { class: 'library-auth-actions' }, [
      el('button', { class: 'library-text-button', onclick: () => showAuth('sign-in') }, [
        'Sign in',
      ]),
      el('button', { class: 'library-primary', onclick: () => showAuth('sign-up') }, [
        'Create account',
      ]),
    ]);
  }

  const accountUser = user;
  const handle = accountUser.handleRequired
    ? 'Set your handle'
    : accountUser.handle
      ? `@${accountUser.handle}`
      : 'Your account';
  const initial = accountUser.handleRequired
    ? 'K'
    : (accountUser.handle ?? 'K').slice(0, 1).toUpperCase();
  return el('div', { class: 'library-account' }, [
    el('div', { class: 'account-copy' }, [
      el('strong', {}, [handle]),
      billing.enabled
        ? el('small', {}, [billing.entitlement.active ? 'Studio plan' : 'Free workspace'])
        : el('small', {}, ['Your workspace']),
    ]),
    el('div', { class: 'account-menu-shell' }, [
      el(
        'button',
        {
          class: 'account-avatar',
          title: 'Account menu',
          'aria-label': `Open account menu for ${handle}`,
          'aria-haspopup': 'menu',
          'aria-expanded': String(accountMenuOpen),
          onclick: () => {
            accountMenuOpen = !accountMenuOpen;
            documentMenuOpen = null;
            render();
          },
        },
        accountUser.image ? [el('img', { src: accountUser.image, alt: '' })] : [initial],
      ),
      accountMenuOpen
        ? el('div', { class: 'library-popover account-menu-popover', role: 'menu' }, [
            el(
              'button',
              {
                type: 'button',
                role: 'menuitem',
                onclick: () => {
                  accountMenuOpen = false;
                  openAccountDialog({
                    user: accountUser,
                    billing,
                    onUser: (next) => {
                      user = next;
                      render();
                    },
                    onSignOut: signOut,
                  });
                  render();
                },
              },
              ['Account settings'],
            ),
            el('button', { type: 'button', role: 'menuitem', onclick: signOut }, ['Sign out']),
          ])
        : el('span'),
    ]),
  ]);
}

async function loadOAuthClient(): Promise<{ name?: string; uri?: string } | null> {
  const clientId = new URLSearchParams(location.search).get('client_id');
  if (!clientId) throw new Error('OAuth consent request is missing its client');
  const response = await fetch(
    `/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
    {
      credentials: 'include',
      headers: { accept: 'application/json' },
    },
  );
  if (!response.ok) throw new Error('This OAuth client is unavailable or no longer registered');
  const client = await response.json();
  return {
    name: typeof client.name === 'string' ? client.name : undefined,
    uri: typeof client.uri === 'string' ? client.uri : undefined,
  };
}

async function submitOAuthConsent(accept: boolean): Promise<void> {
  if (oauthConsentBusy) return;
  oauthConsentBusy = true;
  oauthConsentError = '';
  render();
  try {
    const response = await fetch('/api/auth/oauth2/consent', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ accept, oauth_query: location.search.slice(1) }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.redirect || typeof result.url !== 'string') {
      throw new Error(result?.message ?? 'Could not complete OAuth consent');
    }
    window.location.assign(result.url);
  } catch (error) {
    oauthConsentBusy = false;
    oauthConsentError = (error as Error).message;
    render();
  }
}

function renderOAuthConsent(): HTMLElement {
  const scope = new Set((new URLSearchParams(location.search).get('scope') ?? '').split(/\s+/));
  const ongoingAccess = scope.has('offline_access');
  const permissions = [
    scope.has('kilncat:read')
      ? 'See your persisted KilnCat document list and inspect graphs'
      : null,
    scope.has('kilncat:render') ? 'Render images from documents you can access' : null,
    scope.has('kilncat:write') ? 'Modify persisted documents where you can edit' : null,
    ongoingAccess ? 'Stay connected without asking you to sign in every hour' : null,
  ].filter((value): value is string => !!value);
  const clientName = oauthClient?.name?.trim() || 'An MCP client';
  const safeClientUri = (() => {
    try {
      const url = new URL(oauthClient?.uri ?? '');
      return url.protocol === 'https:' ? url.toString() : null;
    } catch {
      return null;
    }
  })();
  return el('main', { class: 'oauth-consent-page' }, [
    el('section', { class: 'oauth-consent-card', 'aria-labelledby': 'oauth-consent-title' }, [
      el('a', { class: 'oauth-consent-brand', href: '/documents' }, [
        el('span', {}, ['K']),
        'KilnCat',
      ]),
      el('span', { class: 'welcome-kicker' }, ['Authorize MCP access']),
      el('h1', { id: 'oauth-consent-title' }, [`Connect ${clientName}?`]),
      safeClientUri
        ? el(
            'a',
            { class: 'oauth-client-uri', href: safeClientUri, target: '_blank', rel: 'noreferrer' },
            [new URL(safeClientUri).hostname],
          )
        : el('span'),
      el('p', {}, [
        `${clientName} is requesting access as @${user?.handle ?? 'your account'}. It will only see documents your current KilnCat permissions allow.`,
      ]),
      el(
        'ul',
        { class: 'oauth-permissions' },
        permissions.length
          ? permissions.map((permission) => el('li', {}, [permission]))
          : [el('li', {}, ['No recognized KilnCat permissions were requested'])],
      ),
      el('p', { class: 'oauth-consent-note' }, [
        `Document handles identify a document but never bypass sharing, role, billing, or deletion checks. Access tokens expire after one hour.${ongoingAccess ? ' The client can renew them without asking you to sign in again.' : ''}`,
      ]),
      oauthConsentError
        ? el('p', { class: 'library-notice error', role: 'alert' }, [oauthConsentError])
        : el('span'),
      el('div', { class: 'oauth-consent-actions' }, [
        el(
          'button',
          {
            type: 'button',
            class: 'library-secondary',
            disabled: oauthConsentBusy,
            onclick: () => void submitOAuthConsent(false),
          },
          ['Deny'],
        ),
        el(
          'button',
          {
            type: 'button',
            class: 'library-primary',
            disabled: oauthConsentBusy || permissions.length === 0,
            onclick: () => void submitOAuthConsent(true),
          },
          [oauthConsentBusy ? 'Connecting…' : 'Allow access'],
        ),
      ]),
      el('p', { class: 'oauth-legal-links' }, [
        el('a', { href: '/terms', target: '_blank' }, ['Terms']),
        ' · ',
        el('a', { href: '/privacy', target: '_blank' }, ['Privacy']),
      ]),
    ]),
  ]);
}

function renderSignedOut(): HTMLElement {
  const authHost = el('div', { class: 'welcome-auth' });
  openAuthDialog({
    container: authHost,
    dismissible: false,
    googleEnabled: features.googleAuth,
    initialMode: authMode,
    oauthQuery,
    signIn: (email, password) => cloud.signIn(email, password, oauthQuery),
    signUp: (email, password) => cloud.signUp(email, password, oauthQuery),
    onAuthenticated: refresh,
  });

  return el('main', { class: 'library-welcome' }, [
    el('div', { class: 'welcome-glow', 'aria-hidden': 'true' }),
    el('div', { class: 'welcome-layout' }, [
      el('section', { class: 'welcome-card' }, [
        el('span', { class: 'welcome-kicker' }, ['Your material workspace']),
        el('h1', {}, ['Every texture, ready when inspiration hits.']),
        el('p', {}, [
          'Create from a proven template, return to recent work, and invite collaborators—all before the editor loads.',
        ]),
        notice ? el('p', { class: 'library-notice error' }, [notice]) : el('span'),
        !available
          ? el('a', { class: 'library-text-button local-link', href: '/app' }, [
              'Open an unsaved session →',
            ])
          : el('span'),
      ]),
      authHost,
    ]),
  ]);
}

function renderWorkspace(): HTMLElement {
  const normalizedQuery = query.trim().toLowerCase();
  const filtered = documents.filter((document) => {
    const access = document.access ?? 'owner';
    if (view === 'owned' && access !== 'owner') return false;
    if (view === 'shared' && access === 'owner') return false;
    return !normalizedQuery || document.name.toLowerCase().includes(normalizedQuery);
  });

  const navItem = (id: LibraryView, label: string, glyph: string) =>
    el(
      'button',
      {
        class: `library-nav-item${view === id ? ' on' : ''}`,
        type: 'button',
        onclick: () => {
          view = id;
          render();
        },
      },
      [el('span', { 'aria-hidden': 'true' }, [glyph]), label],
    );

  const search = el('input', {
    class: 'library-search',
    type: 'search',
    placeholder: 'Search textures',
    value: query,
    'aria-label': 'Search textures',
  }) as HTMLInputElement;
  search.oninput = () => {
    query = search.value;
    render();
    const next = document.querySelector<HTMLInputElement>('.library-search');
    next?.focus();
    next?.setSelectionRange(query.length, query.length);
  };

  return el('div', { class: 'library-shell' }, [
    el('aside', { class: 'library-sidebar' }, [
      el('a', { class: 'library-wordmark', href: '/' }, [
        el('span', { class: 'library-mark', 'aria-hidden': 'true' }, ['K']),
        el('strong', {}, ['KilnCat']),
      ]),
      el('nav', { class: 'library-nav', 'aria-label': 'Texture library' }, [
        navItem('recent', 'Recent', '◷'),
        navItem('owned', 'My textures', '▦'),
        navItem('shared', 'Shared with me', '♧'),
        el('a', { class: 'library-nav-item', href: '/gallery' }, [
          el('span', { 'aria-hidden': 'true' }, ['△']),
          'Public gallery',
        ]),
      ]),
      el('div', { class: 'sidebar-spacer' }),
      el('div', { class: 'library-legal-links' }, [
        el('a', { href: '/terms' }, ['Terms']),
        el('a', { href: '/privacy' }, ['Privacy']),
      ]),
    ]),
    el('div', { class: 'library-content' }, [
      el('header', { class: 'library-header' }, [search, accountMenu()]),
      el('main', { class: 'library-main' }, [
        notice ? el('div', { class: 'library-notice', role: 'status' }, [notice]) : el('span'),
        el('section', { class: 'template-section' }, [
          el('div', { class: 'section-title' }, [
            el('div', {}, [
              el('h1', {}, ['Start from a template']),
              el('p', {}, ['Choose a foundation, then make it yours.']),
            ]),
          ]),
          el('div', { class: 'template-grid' }, STARTER_TEMPLATES.map(templateCard)),
        ]),
        el('section', { class: 'documents-section' }, [
          !capabilities.cloudWrite
            ? el('div', { class: 'cloud-upgrade-card' }, [
                el('div', {}, [
                  el('strong', {}, ['Upgrade for cloud saves']),
                  el('p', {}, [
                    'Free sessions start blank or from a template and last in the current tab. Export anything you want to keep.',
                  ]),
                ]),
                el(
                  'button',
                  {
                    class: 'library-primary',
                    type: 'button',
                    onclick: () =>
                      openUpgradeDialog({
                        billing,
                        checkout: () => cloud.checkout(),
                      }),
                  },
                  ['Compare Free and Pro'],
                ),
              ])
            : el('span'),
          el('div', { class: 'section-title documents-heading' }, [
            el('div', {}, [
              el('h2', {}, [
                view === 'shared'
                  ? 'Shared with me'
                  : view === 'owned'
                    ? 'My textures'
                    : 'Recent textures',
              ]),
              el('p', {}, [`${filtered.length} texture${filtered.length === 1 ? '' : 's'}`]),
            ]),
            el('button', { class: 'library-secondary', onclick: () => void refresh() }, [
              'Refresh',
            ]),
          ]),
          filtered.length
            ? el('div', { class: 'documents-grid' }, filtered.map(documentCard))
            : el('div', { class: 'documents-empty' }, [
                el('span', { 'aria-hidden': 'true' }, ['◇']),
                el('strong', {}, [
                  normalizedQuery
                    ? 'No matching textures'
                    : view === 'shared'
                      ? 'Nothing shared yet'
                      : capabilities.cloudWrite
                        ? 'Your workspace is ready'
                        : 'Upgrade to save recent textures',
                ]),
                el('p', {}, [
                  normalizedQuery
                    ? 'Try a different search.'
                    : view === 'shared'
                      ? 'Textures shared by collaborators will appear here.'
                      : capabilities.cloudWrite
                        ? 'Pick a template above to create your first texture.'
                        : 'Start a free session from a template above, or choose Pro to keep work here.',
                ]),
              ]),
        ]),
      ]),
    ]),
  ]);
}

function render(): void {
  if (loading) {
    root.replaceChildren(
      el('div', { class: 'library-loading' }, [
        el('span', { class: 'library-mark' }, ['K']),
        'Opening your workspace…',
      ]),
    );
    return;
  }
  root.replaceChildren(
    user ? (oauthConsentRequest ? renderOAuthConsent() : renderWorkspace()) : renderSignedOut(),
  );
}

document.addEventListener('pointerdown', (event) => {
  if (!accountMenuOpen && !documentMenuOpen) return;
  if ((event.target as Element).closest('.account-menu-shell, .document-menu-shell')) return;
  accountMenuOpen = false;
  documentMenuOpen = null;
  render();
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || (!accountMenuOpen && !documentMenuOpen)) return;
  accountMenuOpen = false;
  documentMenuOpen = null;
  render();
});

const requestedAuthMode = new URLSearchParams(location.search).get('auth');
const authMode: 'sign-in' | 'sign-up' = requestedAuthMode === 'sign-up' ? 'sign-up' : 'sign-in';
void refresh().then(() => {
  const search = new URLSearchParams(location.search);
  const ordinaryAuthQuery = [...search.keys()].every((key) => key === 'auth');
  if (location.pathname === DOCUMENT_LIBRARY_PATH && location.search && ordinaryAuthQuery) {
    history.replaceState({}, '', DOCUMENT_LIBRARY_PATH);
  }
});
